import { useMutation } from '@tanstack/react-query';

import { publicFetch } from '../lib/api-client';
import type { SerializedPublicKeyCredentialCreationOptions } from '../lib/webauthn';

/**
 * Activation API Types
 */
export interface VerifyActivationCodeInput {
  email: string;
  activationCode: string;
}

export interface VerifyActivationCodeResponse {
  valid: boolean;
  cognitoUsername: string; // UUID for Cognito username (must be passed to register-passkey)
  registrationOptions: SerializedPublicKeyCredentialCreationOptions;
}

export interface RegisterPasskeyInput {
  email: string;
  activationCode: string;
  cognitoUsername: string; // UUID from verify-activation response
  registrationResponse: {
    id: string;
    rawId: string;
    response: {
      clientDataJSON: string;
      attestationObject: string;
    };
    type: 'public-key';
  };
  expectedChallenge: string;
}

export interface RegisterPasskeyResponse {
  success: boolean;
  userId: string;
  /**
   * Human-readable message from the backend about the registration result.
   * Typically contains success confirmation or additional information.
   */
  message: string;
  credential: {
    id: string;
    transports?: string[];
  };
  tokens: {
    accessToken: string;
    idToken: string;
    refreshToken: string;
  };
}

/**
 * useVerifyActivationCode Hook
 *
 * Verifies an activation code before proceeding to passkey registration.
 * This is a public endpoint (no authentication required).
 *
 * @example
 * ```tsx
 * function ActivationPage() {
 *   const verifyCode = useVerifyActivationCode();
 *
 *   async function handleVerify(email: string, code: string) {
 *     const result = await verifyCode.mutateAsync({ email, activationCode: code });
 *     if (result.valid) {
 *       // Proceed to passkey registration
 *       setStep('passkey');
 *     }
 *   }
 * }
 * ```
 */
export function useVerifyActivationCode() {
  return useMutation({
    mutationFn: (data: VerifyActivationCodeInput) =>
      publicFetch<VerifyActivationCodeResponse>('/v1/auth/verify-activation', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  });
}

/**
 * useRegisterPasskey Hook
 *
 * Registers a passkey credential after activation code verification.
 * This endpoint receives the WebAuthn credential data from the browser.
 *
 * @example
 * ```tsx
 * function PasskeySetup() {
 *   const registerPasskey = useRegisterPasskey();
 *
 *   async function handleSetup() {
 *     // Create WebAuthn credential
 *     const credential = await navigator.credentials.create({...});
 *
 *     // Register with backend
 *     await registerPasskey.mutateAsync({
 *       email,
 *       activationCode,
 *       credentialId: credential.id,
 *       publicKey: credential.response.getPublicKey(),
 *     });
 *   }
 * }
 * ```
 */
export function useRegisterPasskey() {
  return useMutation({
    mutationFn: (data: RegisterPasskeyInput) =>
      publicFetch<RegisterPasskeyResponse>('/v1/auth/register-passkey', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  });
}
